import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Overlay, Popover, Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/free-solid-svg-icons'
import { snakeCase } from 'lodash'
import { useErrorContext } from 'contexts/ErrorContext'
import AccountCodePicker, { accountCodeType } from './AccountCodePicker'

const AccountFilterableHeader = ({
  accountType,
  title,
  params,
  setParams,
  includeOrdering = true,
  popoverId = '',
  activePopoverId = null,
  setActivePopoverId = null,
  dashboardSetParams = null,
  canEdit = true
}) => {
  const { setErrorContext, clearErrorContext } = useErrorContext()

  useEffect(() => {
    if (accountType) {
      setErrorContext({ column: accountType })
    }
    return () => clearErrorContext()
  }, [accountType, setErrorContext, clearErrorContext])
  const [pendingParams, setPendingParams] = useState(params)
  const showPopover = activePopoverId === popoverId
  const target = useRef(null)
  const overlayTarget = useRef(null)

  const codeType = accountCodeType(accountType)
  const operatorKey = codeType + 'Operator'
  const searchKey = `${snakeCase(codeType)}_search`

  const handleTogglePopover = useCallback((show) => {
    if (setActivePopoverId) setActivePopoverId(show ? popoverId : null)
  }, [setActivePopoverId, popoverId])

  const isFiltered = params[codeType] ? params[codeType].length > 0 : false

  // Sync pendingParams with params when params change and popover is closed
  // This ensures the filter state is correct when dashboard reloads from cache
  useEffect(() => {
    if (!showPopover) {
      setPendingParams(params)
    }
  }, [params, showPopover])

  // Handle clicking outside to close popover
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        showPopover &&
        overlayTarget.current &&
        !overlayTarget.current.contains(event.target) &&
        !target.current.contains(event.target)
      ) {
        handleTogglePopover(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showPopover, handleTogglePopover])

  const handleToggleCode = (accountCode, checked) => {
    setPendingParams((prev) => ({
      ...prev,
      [codeType]: checked ? [...(prev[codeType] || []), accountCode.id] : (prev[codeType] || []).filter((id) => id !== accountCode.id),
      page: 1
    }))
  }

  const handleOperatorToggle = () => {
    setPendingParams((prev) => ({
      ...prev,
      [operatorKey]: prev[operatorKey] === 'include' ? 'exclude' : 'include'
    }))
  }

  const handleApply = () => {
    if (!canEdit) {
      handleTogglePopover(false)
      return
    }
    const applied = (current) => ({
      ...current,
      [codeType]: pendingParams[codeType],
      [operatorKey]: pendingParams[operatorKey],
      [searchKey]: pendingParams[searchKey],
      page: 1
    })
    setParams(applied)
    if (dashboardSetParams) dashboardSetParams(applied)
    handleTogglePopover(false)
  }

  const renderFilterPopover = () => (
    <Overlay target={target.current} show={showPopover} placement="bottom" onHide={handleApply} rootClose>
      <Popover
        id={`popover-account-${accountType}`}
        style={{ maxHeight: 400, minWidth: '28%', overflowY: 'auto' }}
        className="shadow-sm"
        ref={overlayTarget}
      >
        <AccountCodePicker
          accountType={accountType}
          selectedIds={pendingParams[codeType] || []}
          operator={pendingParams[operatorKey]}
          onToggleCode={handleToggleCode}
          onToggleOperator={handleOperatorToggle}
          initialSearch={pendingParams[searchKey] || ''}
          onSearchChange={(search) => setPendingParams((prev) => ({ ...prev, [searchKey]: search }))}
          queryParams={pendingParams}
          canEdit={canEdit}
          actions={
            <Button size="sm" variant="primary" onClick={handleApply} disabled={!canEdit}>
              {canEdit ? 'Apply' : 'Close'}
            </Button>
          }
        />
        <Popover.Body className="text-center"></Popover.Body>
      </Popover>
    </Overlay>
  )

  return (
    <th style={{ cursor: 'default' }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        {title}
        <Button
          ref={target}
          variant={isFiltered ? 'primary' : 'light'}
          size="sm"
          className="ms-1 border-0"
          style={{
            padding: '2px 6px',
            minWidth: 'auto'
          }}
          onClick={(e) => {
            e.stopPropagation()
            showPopover ? handleApply() : handleTogglePopover(!showPopover)
          }}
          title={!canEdit ? 'View only - only the creator or permitted users can edit filters' : ''}
          data-testid={`account-filter-${accountType}`}
        >
          <FontAwesomeIcon icon={faFilter} size="xs" />
        </Button>
        {renderFilterPopover()}
      </span>
    </th>
  )
}

export default AccountFilterableHeader